import { useCallback, useEffect, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import {
	type AnalyticsEventPayload,
	UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';

import {
	type EditionAwarenessContextType,
	useEditionAwareness,
} from '../services/edition-awareness-context';
import { useExperimentContext } from '../services/experiment-context';

export const ANALYTICS_CHANNEL: FabricChannel.growth = FabricChannel.growth;

type EditionAwarenessAnalyticsContextType = Omit<
	EditionAwarenessContextType,
	'edition' | 'invoiceableStatus'
> & {
	billingSourceSystem: 'CCP' | null; // CCP is now the only supported billing system
	currentProductEdition: EditionAwarenessContextType['edition']; // currentProductEdition is part of Common Instrumentation Event Attribute Spec
	isInvoiceable: boolean | null; // We use the invoiceableStatus for checking the value, but send isInvoiceable as part of analytics
};

export const getAttributesFromContexts = (context: unknown): Record<string, any> => {
	if (!context || !Array.isArray(context)) {
		return {};
	}

	return context.reduce((acc, x) => {
		if (x.attributes) {
			return {
				...acc,
				...x.attributes,
			};
		}

		return {
			...acc,
			...(x ?? {}),
		};
	}, {});
};

export const defaultAttributes = {
	packageName: '@atlassian/edition-awareness',
	source: 'editionAwareness',
	featureId: 'edition-awareness',
	growthPillar: 'BUY',
};

export function useAnalytics(): {
	sendAnalyticsEvent: (payload: AnalyticsEventPayload | UIAnalyticsEvent) => void;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const editionAwarenessContext = useEditionAwareness();
	const experimentContext = useExperimentContext();

	const sendAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload | UIAnalyticsEvent): void => {
			const editionAwarenessContextAttributes: EditionAwarenessAnalyticsContextType = {
				// Required properties
				cloudId: editionAwarenessContext.cloudId,
				product: editionAwarenessContext.product,
				currentProductEdition: editionAwarenessContext.edition,

				// Payment properties
				isInvoiceable:
					editionAwarenessContext.invoiceableStatus === 'INVOICEABLE'
						? true
						: editionAwarenessContext.invoiceableStatus === 'NOT_INVOICEABLE'
							? false
							: null,
				hasPaymentDetailsEntered: editionAwarenessContext.hasPaymentDetailsEntered,
				canAddPaymentDetails: editionAwarenessContext.canAddPaymentDetails,
				addPaymentDetailsExperienceUrl: editionAwarenessContext.addPaymentDetailsExperienceUrl,

				// Trial properties
				trialDaysLeft: editionAwarenessContext.trialDaysLeft,
				trialLengthDays: editionAwarenessContext.trialLengthDays,
				trialEndDate: editionAwarenessContext.trialEndDate,
				isInTrial: editionAwarenessContext.isInTrial,
				isInPredunning: editionAwarenessContext.isInPredunning,
				isInReverseTrial: editionAwarenessContext.isInReverseTrial,
				trialOfferingName: editionAwarenessContext.trialOfferingName,
				lastKnownTrial: editionAwarenessContext?.lastKnownTrial,
				offeringKey: editionAwarenessContext?.offeringKey,

				// Upgrade properties
				canUpgrade: editionAwarenessContext.canUpgrade,
				changeOfferingToStandardExperienceUrl:
					editionAwarenessContext.changeOfferingToStandardExperienceUrl,
				directBuyChangeOfferingToStandardExperienceUrl:
					editionAwarenessContext.directBuyChangeOfferingToStandardExperienceUrl,

				// Entitlement properties
				entitlementId: editionAwarenessContext.entitlementId,
				isInCollection: editionAwarenessContext.isInCollection,
				collectionType: editionAwarenessContext.collectionType,

				// User permissions properties
				userRoles: editionAwarenessContext.userRoles,
				userEmail: editionAwarenessContext.userEmail,

				// Site properties
				billingSourceSystem: editionAwarenessContext.hasDismissedButton === false ? null : 'CCP', // preserve nulls for EA Upsell
				licensedUsers: editionAwarenessContext.licensedUsers,
				storageUsageInGB: editionAwarenessContext.storageUsageInGB,
				isEnterpriseCustomer: editionAwarenessContext.isEnterpriseCustomer,

				// Site traits
				automationUsage: editionAwarenessContext.automationUsage,
				lastAutomationFreeLimitBreachDate:
					editionAwarenessContext.lastAutomationFreeLimitBreachDate,
				automationFreeLimitStatus: editionAwarenessContext.automationFreeLimitStatus,

				// Capabilities
				shouldAutoOpen: editionAwarenessContext.shouldAutoOpen,

				// For twcg-1563 experiment, temporary attribute for the introduction of full JWM support
				originalProjectTypeWasCore: editionAwarenessContext.originalProjectTypeWasCore,
			};

			if (payload instanceof UIAnalyticsEvent) {
				const contextAttributes = getAttributesFromContexts(payload.context);

				payload
					.update({
						...payload.payload,
						attributes: {
							...contextAttributes,
							...defaultAttributes,
							...payload.payload.attributes,
							...editionAwarenessContextAttributes,
							experiments: experimentContext.experiments(),
						},
					})
					.fire(ANALYTICS_CHANNEL);
			} else {
				const event = createAnalyticsEvent({
					...payload,
					attributes: {
						...defaultAttributes,
						...payload.attributes,
						...editionAwarenessContextAttributes,
						experiments: experimentContext.experiments(),
					},
				});

				const contextAttributes = getAttributesFromContexts(event.context);

				event
					.update({
						attributes: {
							...contextAttributes,
							...event.payload.attributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			}
		},
		[createAnalyticsEvent, editionAwarenessContext, experimentContext],
	);

	return {
		sendAnalyticsEvent,
	};
}

/**
 * Put at the end of your render tree, will fire a screen event once when first rendered with the `name`
 */
export function ScreenEvent({
	name,
	attributes,
}: {
	attributes?: Record<string, unknown>;
	name: string;
}) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}
		sendAnalyticsEvent({
			eventType: 'screen',
			name,
			attributes,
		});
		hasSent.current = true;
	}, [sendAnalyticsEvent, name, attributes]);

	return null;
}
